import React, { useState } from 'react';
import { ChevronDown } from 'lucide-react';

interface ReactionCard {
  part: 'A' | 'B' | 'C';
  type: string;
  equation: string;
  prettyEquation: React.ReactNode;
  observations: string[];
  explanation: string;
  accentClass: string;
}

const CARDS: ReactionCard[] = [
  {
    part: 'A',
    type: 'Thermal decomposition',
    equation: 'CuCO3(s) -> CuO(s) + CO2(g)',
    prettyEquation: (
      <>CuCO<sub>3</sub>(s) → CuO(s) + CO<sub>2</sub>(g)</>
    ),
    observations: [
      'Green CuCO₃ turns black (CuO is formed).',
      'CO₂ gas turns limewater milky.',
    ],
    explanation:
      'Heat breaks down copper(II) carbonate into two simpler products: a black solid (copper(II) oxide) and a colourless gas (carbon dioxide). This is a classic thermal decomposition of a metal carbonate.',
    accentClass: 'border-emerald-500/30 bg-emerald-500/5',
  },
  {
    part: 'B',
    type: 'Gas-phase acid–base',
    equation: 'HCl(g) + NH3(g) -> NH4Cl(s)',
    prettyEquation: (
      <>HCl(g) + NH<sub>3</sub>(g) → NH<sub>4</sub>Cl(s)</>
    ),
    observations: [
      'Dense white smoke forms in the space between the test tubes.',
      'The smoke is solid ammonium chloride.',
    ],
    explanation:
      'Concentrated HCl and concentrated ammonia are both volatile — they emit gaseous HCl and NH₃ at room temperature. When the two gases meet they react instantly, forming solid NH₄Cl as visible smoke (an aerosol of fine crystals).',
    accentClass: 'border-violet-500/30 bg-violet-500/5',
  },
  {
    part: 'C',
    type: 'Precipitation (metathesis)',
    equation: 'Pb(NO3)2(aq) + 2KI(aq) -> PbI2(s) + 2KNO3(aq)',
    prettyEquation: (
      <>Pb(NO<sub>3</sub>)<sub>2</sub>(aq) + 2KI(aq) → PbI<sub>2</sub>(s) + 2KNO<sub>3</sub>(aq)</>
    ),
    observations: [
      'A bright yellow precipitate of PbI₂ forms instantly.',
      'KNO₃ stays dissolved (colourless solution).',
    ],
    explanation:
      'Both reactants are colourless aqueous solutions. When mixed, Pb²⁺ and I⁻ ions meet and form an insoluble compound (PbI₂) that drops out of solution as a vivid yellow precipitate. The K⁺ and NO₃⁻ ions stay in solution as spectator ions.',
    accentClass: 'border-amber-500/30 bg-amber-500/5',
  },
];

export function BalancedEquationsReactions() {
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  return (
    <div className="flex flex-col h-full">
      <h2 className="text-lg font-bold mb-1">Reactions Reference</h2>
      <p className="text-xs text-muted-foreground mb-3">
        The three balanced equations for Activity 3.18, with the visible
        observations and a brief explanation of the mechanism.
      </p>
      <div className="flex flex-col gap-3">
        {CARDS.map(card => (
          <div key={card.part} className={`p-3 rounded-xl border ${card.accentClass}`}>
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[10px] font-bold tracking-wider uppercase text-muted-foreground">Part {card.part} · {card.type}</span>
            </div>
            <div className="font-mono text-base text-foreground mb-2">
              {card.prettyEquation}
            </div>
            <ul className="text-xs text-muted-foreground list-disc list-inside ml-1 mb-2">
              {card.observations.map((obs, i) => (
                <li key={i}>{obs}</li>
              ))}
            </ul>
            <button
              onClick={() => setExpanded(p => ({ ...p, [card.part]: !p[card.part] }))}
              className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wider text-foreground hover:opacity-80"
            >
              <ChevronDown className={`w-3 h-3 transition-transform ${expanded[card.part] ? 'rotate-180' : ''}`} />
              {expanded[card.part] ? 'Hide explanation' : 'Show explanation'}
            </button>
            {expanded[card.part] && (
              <p className="text-xs text-muted-foreground mt-2 leading-relaxed">{card.explanation}</p>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

export default BalancedEquationsReactions;
